import { useMemo, useState } from 'react';
import { Button } from '@/core/components/ui/button';
import { Skeleton } from '@/core/components/ui/skeleton';
import { Badge } from '@/core/components/ui/badge';
import {
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  GripVertical,
} from 'lucide-react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Language } from '../../types/translation-types';
import { ApplicationLanguage } from '../../types/language-management-types';
import { cn } from '@/core/lib/utils';
import { parseCompoundLanguageCode } from '../../utils/language-code';

interface AppLanguagesTransferListProps {
  availableLanguages: Language[];
  assignedLanguageIds: number[];
  applicationLanguages: ApplicationLanguage[];
  onAdd: (languageId: number) => void;
  onRemove: (languageId: number) => void;
  onReorder: (languageIds: number[]) => void;
  isLoading: boolean;
}

interface LanguageItemData {
  languageId: number;
  languageCode: string;
  languageName: string;
  nativeName: string;
  isRtl: boolean;
}

// Draggable Available Language Item
function DraggableAvailableLanguage({
  lang,
  onAdd,
}: {
  lang: Language;
  onAdd: (languageId: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `available-${lang.languageId}`,
    data: { lang, source: 'available' },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const { operatorName } = parseCompoundLanguageCode(lang.languageCode);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-move',
        isDragging && 'z-50'
      )}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-center gap-3">
        <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className="font-mono text-xs text-gray-500 w-8">{lang.languageCode}</span>
        <div>
          <div className="font-medium text-sm">
            {lang.languageName}
            {operatorName && <span className="ml-1 text-xs text-gray-500">({operatorName})</span>}
          </div>
          {lang.nativeName !== lang.languageName && (
            <div className="text-xs text-gray-500">{lang.nativeName}</div>
          )}
        </div>
        {lang.isRtl && (
          <Badge variant="secondary" className="text-xs">
            RTL
          </Badge>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onAdd(lang.languageId);
        }}
        className="h-8 w-8 p-0"
        title="Add to application"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Sortable Assigned Language Item
function SortableAssignedLanguage({
  lang,
  index,
  totalItems,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  lang: LanguageItemData;
  index: number;
  totalItems: number;
  onRemove: (languageId: number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `assigned-${lang.languageId}`,
    data: { lang, source: 'assigned' },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const { operatorName } = parseCompoundLanguageCode(lang.languageCode);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-move',
        index === 0 && 'bg-amber-50',
        isDragging && 'z-50'
      )}
    >
      <div className="flex items-center gap-3" {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />
        <span className="font-mono text-xs text-gray-500 w-8">{lang.languageCode}</span>
        <div>
          <div className="font-medium text-sm">
            {lang.languageName}
            {operatorName && <span className="ml-1 text-xs text-gray-500">({operatorName})</span>}
            {index === 0 && <span className="ml-2 text-xs text-amber-600">(Default)</span>}
          </div>
          {lang.nativeName !== lang.languageName && (
            <div className="text-xs text-gray-500">{lang.nativeName}</div>
          )}
        </div>
        {lang.isRtl && (
          <Badge variant="secondary" className="text-xs">
            RTL
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp(index);
          }}
          disabled={index === 0}
          className="h-7 w-7 p-0"
          title="Move up"
        >
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown(index);
          }}
          disabled={index === totalItems - 1}
          className="h-7 w-7 p-0"
          title="Move down"
        >
          <ChevronDown className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(lang.languageId);
          }}
          className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
          title="Remove from application"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export function AppLanguagesTransferList({
  availableLanguages,
  assignedLanguageIds,
  applicationLanguages,
  onAdd,
  onRemove,
  onReorder,
  isLoading,
}: AppLanguagesTransferListProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px drag distance before activating
      },
    })
  );

  // Get available (unassigned) languages
  const unassignedLanguages = useMemo(() => {
    return availableLanguages.filter((lang) => !assignedLanguageIds.includes(lang.languageId));
  }, [availableLanguages, assignedLanguageIds]);

  // Get assigned languages with full details, maintaining order
  const assignedLanguages = useMemo(() => {
    return assignedLanguageIds
      .map((id) => {
        // First check applicationLanguages for the data
        const appLang = applicationLanguages.find((l) => l.languageId === id);
        if (appLang) {
          return {
            languageId: appLang.languageId,
            languageCode: appLang.languageCode,
            languageName: appLang.languageName,
            nativeName: appLang.nativeName,
            isRtl: appLang.isRtl,
          };
        }
        // Fall back to availableLanguages
        const lang = availableLanguages.find((l) => l.languageId === id);
        if (lang) {
          return {
            languageId: lang.languageId,
            languageCode: lang.languageCode,
            languageName: lang.languageName,
            nativeName: lang.nativeName,
            isRtl: lang.isRtl,
          };
        }
        return null;
      })
      .filter(Boolean) as LanguageItemData[];
  }, [assignedLanguageIds, applicationLanguages, availableLanguages]);

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newOrder = [...assignedLanguageIds];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    onReorder(newOrder);
  };

  const handleMoveDown = (index: number) => {
    if (index === assignedLanguageIds.length - 1) return;
    const newOrder = [...assignedLanguageIds];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    onReorder(newOrder);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Don't do anything if dropping on itself
    if (active.id === over.id) return;

    // Dragging from available to assigned
    if (activeData?.source === 'available' && overData?.source === 'assigned') {
      const langId = activeData.lang.languageId;
      if (!assignedLanguageIds.includes(langId)) {
        onAdd(langId);
      }
    }

    // Dragging from assigned to available
    if (activeData?.source === 'assigned' && overData?.source === 'available') {
      const langId = activeData.lang.languageId;
      if (assignedLanguageIds.includes(langId)) {
        onRemove(langId);
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeData = active.data.current;
    const overData = over.data.current;

    // Reordering within assigned list
    if (activeData?.source === 'assigned' && overData?.source === 'assigned') {
      const activeIndex = assignedLanguageIds.indexOf(activeData.lang.languageId);
      const overIndex = assignedLanguageIds.indexOf(overData.lang.languageId);

      if (activeIndex !== overIndex) {
        const newOrder = arrayMove(assignedLanguageIds, activeIndex, overIndex);
        onReorder(newOrder);
      }
    }

    // Dragging from available to assigned (drop on container)
    if (activeData?.source === 'available' && active.id !== over.id) {
      const langId = activeData.lang.languageId;
      if (!assignedLanguageIds.includes(langId)) {
        onAdd(langId);
      }
    }
  };

  // Get the active item being dragged
  const activeDragItem = useMemo(() => {
    if (!activeId) return null;

    if (activeId.startsWith('available-')) {
      const langId = parseInt(activeId.replace('available-', ''));
      return unassignedLanguages.find((l) => l.languageId === langId);
    }

    if (activeId.startsWith('assigned-')) {
      const langId = parseInt(activeId.replace('assigned-', ''));
      return assignedLanguages.find((l) => l.languageId === langId);
    }

    return null;
  }, [activeId, unassignedLanguages, assignedLanguages]);

  const activeDragItemOperatorName = activeDragItem
    ? parseCompoundLanguageCode(activeDragItem.languageCode).operatorName
    : null;

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-8">
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <div className="border rounded-md p-4 space-y-2 h-[400px]">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <div className="border rounded-md p-4 space-y-2 h-[400px]">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="grid grid-cols-2 gap-8">
        {/* Available Languages */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm text-gray-700">
            Available Languages ({unassignedLanguages.length})
          </h3>
          <div className="border rounded-md min-h-[400px] max-h-[500px] overflow-auto">
            {unassignedLanguages.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                All languages are assigned to this application
              </div>
            ) : (
              <SortableContext
                items={unassignedLanguages.map((lang) => `available-${lang.languageId}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="divide-y">
                  {unassignedLanguages.map((lang) => (
                    <DraggableAvailableLanguage
                      key={lang.languageId}
                      lang={lang}
                      onAdd={onAdd}
                    />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>
        </div>

        {/* Assigned Languages */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm text-gray-700">
            Assigned Languages ({assignedLanguages.length})
          </h3>
          <div className="border rounded-md min-h-[400px] max-h-[500px] overflow-auto">
            {assignedLanguages.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                <p>No languages assigned</p>
                <p className="mt-1 text-xs">
                  If no languages are configured, all active languages will be used by default
                </p>
              </div>
            ) : (
              <SortableContext
                items={assignedLanguages.map((lang) => `assigned-${lang.languageId}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="divide-y">
                  {assignedLanguages.map((lang, index) => (
                    <SortableAssignedLanguage
                      key={lang.languageId}
                      lang={lang}
                      index={index}
                      totalItems={assignedLanguages.length}
                      onRemove={onRemove}
                      onMoveUp={handleMoveUp}
                      onMoveDown={handleMoveDown}
                    />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-2">
            The first language is the default. Drag to reorder or use arrows.
          </p>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeDragItem ? (
          <div className="flex items-center gap-3 p-3 bg-white border rounded shadow-lg">
            <GripVertical className="h-4 w-4 text-gray-400" />
            <span className="font-mono text-xs text-gray-500 w-8">
              {activeDragItem.languageCode}
            </span>
            <div>
              <div className="font-medium text-sm">
                {activeDragItem.languageName}
                {activeDragItemOperatorName && (
                  <span className="ml-1 text-xs text-gray-500">({activeDragItemOperatorName})</span>
                )}
              </div>
              {activeDragItem.nativeName !== activeDragItem.languageName && (
                <div className="text-xs text-gray-500">{activeDragItem.nativeName}</div>
              )}
            </div>
            {activeDragItem.isRtl && (
              <Badge variant="secondary" className="text-xs">
                RTL
              </Badge>
            )}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
